import React from 'react'
import {
  List,
  useTable,
  ShowButton,
  EditButton,
  DeleteButton,
  DateField,
  CreateButton,
} from '@refinedev/antd'
import { useList } from '@refinedev/core'
import { Table, Space } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { useRowShow, ListPage, Pill, TableSurface, type Stat } from '../../theme/listkit'
import { Newspaper, FileText, CalendarDays } from '../../components/icons'

type Article = Tables<'articles'>

const fmtDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—'

export function ArticleList() {
  const { tableProps } = useTable<Article>({
    syncWithLocation: true,
    sorters: {
      initial: [{ field: 'created_at', order: 'desc' }],
    },
  })
  const onRow = useRowShow('articles')

  // Bounded summary deck — a single pre-aggregated row from the admin summary
  // view (counts + distinct categories + latest created_at) instead of pulling
  // the whole `articles` table to the client and reducing on the main thread.
  const { result: summaryRes } = useList<{
    total_articles: number | null
    distinct_categories: number | null
    latest_created_at: string | null
  }>({
    resource: 'admin_articles_summary',
    pagination: { pageSize: 1 },
  })
  const summary = summaryRes?.data?.[0]
  const totalArticles = summary?.total_articles ?? 0
  const categoryCount = summary?.distinct_categories ?? 0
  const latest = summary?.latest_created_at ?? null

  const stats: Stat[] = [
    { label: 'Total Articles', value: totalArticles.toLocaleString(), icon: Newspaper, tone: 'gold' },
    { label: 'Categories', value: categoryCount.toLocaleString(), icon: FileText, tone: 'jaguar' },
    {
      label: 'Latest',
      value: totalArticles ? 'Published' : '—',
      hint: latest ? fmtDate(latest) : undefined,
      icon: CalendarDays,
      tone: 'green',
    },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Content · News & Articles',
        description:
          'Stories, announcements, and news shared with the alumni community. Curate the front-page reading list — each piece carries a category and an estimated read time.',
        actions: <CreateButton />,
      }}
      stats={stats}
      statColumns={3}
    >
      <List title={false} breadcrumb={false}>
        <TableSurface>
          <Table {...tableProps} rowKey="id" scroll={{ x: true }} onRow={onRow}>
            <Table.Column<Article>
              dataIndex="title"
              title="Title"
              render={(title: string) => (
                <span style={{ fontFamily: brand.fSans, fontWeight: 600, color: brand.ink }}>{title}</span>
              )}
            />
            <Table.Column<Article>
              dataIndex="category"
              title="Category"
              render={(category: string | null) =>
                category ? <Pill tone="jaguar">{category}</Pill> : <span style={{ color: brand.sub }}>—</span>
              }
            />
            <Table.Column<Article>
              dataIndex="date"
              title="Date"
              render={(date: string | null) =>
                date ? (
                  <span style={{ fontFamily: brand.fSans, fontWeight: 600, color: brand.sub }}>{date}</span>
                ) : (
                  <span style={{ color: brand.sub }}>—</span>
                )
              }
            />
            <Table.Column<Article>
              dataIndex="read_time"
              title="Read Time"
              render={(rt: string | null) =>
                rt ? (
                  <span style={{ fontFamily: brand.fSans, fontWeight: 500, color: brand.sub }}>{rt}</span>
                ) : (
                  <span style={{ color: brand.sub }}>—</span>
                )
              }
            />
            <Table.Column<Article>
              dataIndex="created_at"
              title="Created"
              sorter
              render={(value: string) => <DateField value={value} format="MMM D, YYYY" />}
            />
            <Table.Column<Article>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, record: Article) => (
                <Space>
                  <ShowButton hideText size="small" recordItemId={record.id} />
                  <EditButton hideText size="small" recordItemId={record.id} />
                  <DeleteButton hideText size="small" recordItemId={record.id} />
                </Space>
              )}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
